/**
 * dealPeople.ts — the pure half of People on this deal (deal detail slice 8;
 * spec §3.5 A): the single-threaded warning's sentence (its rule is the
 * server's since §40.12).
 */
import type { DealDetail } from '../../types'
import type { DealContactRole, SalesDealContact } from '../../../../api/types'
import { compactMoney } from '../../../../lib/money'
import { STAGE_STEPS } from './stageBar'

/** The deal's fenced GBP value (salesarith's fence, in the browser for the
 *  sentence's figure only): GBP → its own value; non-GBP → the captured base;
 *  uncaptured → null (nothing is invented). */
export function fencedPence(d: Pick<DealDetail, 'currency' | 'amountPence' | 'baseValuePence'>): number | null {
  if (d.currency === 'GBP') return d.amountPence
  return d.baseValuePence ?? null
}

/** The single-threaded warning's sentence, or null when it does not apply.
 *  WHETHER it applies is the SERVER's (§40.12 rule set 2: a £1M+ deal with
 *  exactly one linked contact — the Contact check's `single_threaded`
 *  reason), so the warning, the health card and the board badge agree. */
export function singleThreadedWarning(d: Pick<DealDetail, 'currency' | 'amountPence' | 'baseValuePence' | 'stage' | 'assessment'>, canAdd = true): string | null {
  const contact = d.assessment?.checks.find(c => c.id === 'contact')
  if (contact?.reason !== 'single_threaded') return null
  const v = fencedPence(d)
  if (v == null) return null
  // "…before Proposal" is true only while the deal is still before it.
  const beforeProposal = STAGE_STEPS.indexOf(d.stage) >= 0 && STAGE_STEPS.indexOf(d.stage) < STAGE_STEPS.indexOf('Proposal Sent')
  if (!canAdd) return `One contact on a ${compactMoney(v, 'GBP')} deal.`
  return `One contact on a ${compactMoney(v, 'GBP')} deal. Add a second person, such as procurement${beforeProposal ? ', before Proposal' : ''}.`
}

/** The rule set whose api serves R6's roles (0139; internal/modules/sales/health
 *  RuleSetVersion 3 added the committee family together with the role route).
 *  The running api before R6 evaluates rule set 2. */
export const ROLES_RULE_SET = 3

/** Whether the api behind this page serves buying-committee roles (R6) — read
 *  from the wire, never assumed: the deal's assessment carries rule set ≥ 3,
 *  or a link already carries a role (only an R6 api sends one). Without it the
 *  People card shows no picker and no coverage line, so nothing claims roles
 *  exist on an api that has none (ruling of 2026-10-24, option C). A closed
 *  deal has no assessment: roles show there only once a link carries one. */
export function rolesSupported(
  d: Pick<DealDetail, 'assessment'>,
  items: readonly Pick<SalesDealContact, 'role'>[],
): boolean {
  return (d.assessment?.ruleSet ?? 0) >= ROLES_RULE_SET || items.some(i => Boolean(i.role))
}

/** R6 (0139) + AD4 (0147): the buying-committee roles in the People card's order, with
 *  their labels. A person who plays two parts takes the more decisive role
 *  (ruling 1) — one role per contact per deal. */
export const COMMITTEE_ROLES: readonly { role: DealContactRole; label: string }[] = [
  { role: 'decision_maker', label: 'Decision maker' },
  { role: 'champion', label: 'Champion' },
  { role: 'technical_evaluator', label: 'Technical evaluator' },
  { role: 'finance', label: 'Finance' },
  { role: 'procurement', label: 'Procurement' },
  { role: 'user', label: 'User' },
  { role: 'blocker', label: 'Blocker' },
]

export function roleLabel(role: string | null | undefined): string | null {
  return COMMITTEE_ROLES.find(r => r.role === role)?.label ?? null
}

/** The coverage line: each role someone holds, in order, with who holds it;
 *  and whether a decision maker is linked. Names only as the links read
 *  carries them — nothing counted per person. */
export function committeeCoverage(items: readonly Pick<SalesDealContact, 'name' | 'role'>[]): { held: { label: string; names: string[] }[]; hasDecisionMaker: boolean } {
  const held = COMMITTEE_ROLES
    .map(r => ({ label: r.label, names: items.filter(i => i.role === r.role).map(i => i.name) }))
    .filter(r => r.names.length > 0)
  return { held, hasDecisionMaker: items.some(i => i.role === 'decision_maker') }
}

/** The coverage line as one sentence (the card renders exactly this): the
 *  missing decision maker first when there is none, then every held role. */
export function coverageSentence(items: readonly Pick<SalesDealContact, 'name' | 'role'>[]): string {
  const { held, hasDecisionMaker } = committeeCoverage(items)
  const parts = held.map(h => `${h.label}: ${h.names.join(', ')}`)
  if (!hasDecisionMaker) parts.unshift('No decision maker set')
  return parts.join(' · ')
}
